import React from 'react';
import { Typography, Paper, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';

const SecuritySettings = () => {
  const securityFeatures = [
    {
      icon: <LockIcon />,
      title: "Password Management",
      description: "Set strong password requirements and enable regular password changes for enhanced security."
    },
    {
      icon: <VerifiedUserIcon />,
      title: "Two-Factor Authentication (2FA)",
      description: "Enable and manage 2FA to add an extra layer of security to user accounts."
    },
    {
      icon: <VpnKeyIcon />,
      title: "API Key Management",
      description: "Generate, revoke, and manage API keys for secure integrations with other systems."
    },
    {
      icon: <VisibilityOffIcon />,
      title: "Data Privacy Controls",
      description: "Configure data retention policies and manage user data in compliance with privacy regulations."
    },
    {
      icon: <SecurityIcon />,
      title: "Security Logs and Alerts",
      description: "Monitor security events and set up alerts for suspicious activities."
    }
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>Security Settings</Typography>
      <Typography paragraph>
        The Security Settings section provides robust tools and options to enhance the security of your AI Talker account 
        and protect your sensitive data. Implementing strong security measures is crucial for maintaining the integrity 
        of your AI Talker system and ensuring compliance with data protection regulations.
      </Typography>

      <List>
        {securityFeatures.map((feature, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              {feature.icon}
            </ListItemIcon>
            <ListItemText 
              primary={feature.title}
              secondary={feature.description}
            />
          </ListItem>
        ))}
      </List>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Key Security Measures:</Typography>
      <Typography paragraph>
        1. <strong>Strong Password Policies:</strong> Enforce complex passwords with a combination of uppercase and lowercase letters, 
        numbers, and special characters. Set password expiration policies to ensure regular updates.
      </Typography>
      <Typography paragraph>
        2. <strong>Two-Factor Authentication:</strong> Encourage or require 2FA for all user accounts, especially for 
        administrators and users with high-level permissions.
      </Typography>
      <Typography paragraph>
        3. <strong>API Security:</strong> Carefully manage API keys, use HTTPS for all API communications, and implement 
        rate limiting to prevent abuse.
      </Typography>
      <Typography paragraph>
        4. <strong>Data Encryption:</strong> Ensure all sensitive data is encrypted both in transit and at rest using 
        industry-standard encryption protocols.
      </Typography>
      <Typography paragraph>
        5. <strong>Access Controls:</strong> Implement role-based access control (RBAC) to ensure users only have access 
        to the data and features necessary for their role.
      </Typography>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Best Practices for Security:</Typography>
      <Typography paragraph>
        - Regularly review and update your security settings to ensure they meet current best practices and comply with relevant regulations.
      </Typography>
      <Typography paragraph>
        - Conduct periodic security audits to identify and address potential vulnerabilities in your AI Talker implementation.
      </Typography>
      <Typography paragraph>
        - Provide security awareness training to all users to help them understand their role in maintaining system security.
      </Typography>
      <Typography paragraph>
        - Keep all systems and integrations up-to-date with the latest security patches and updates.
      </Typography>
      <Typography paragraph>
        - Implement a robust incident response plan to quickly address any security breaches or suspicious activities.
      </Typography>

      <Typography paragraph style={{ marginTop: '20px' }}>
        Security is an ongoing process, and it's important to stay vigilant and proactive in protecting your AI Talker 
        system. Regularly reviewing and updating your security settings is crucial to maintaining the confidentiality, 
        integrity, and availability of your data.
      </Typography>
      <Typography paragraph>
        If you have any questions or concerns about your account's security, or if you need assistance in implementing 
        advanced security measures, our dedicated security team is always available to help. We're committed to ensuring 
        that your AI Talker experience is not only powerful and efficient but also secure and compliant with industry standards.
      </Typography>
    </Paper>
  );
};

export default SecuritySettings;
